import { apiService } from "api/apiService";
import type {
  TeacherPortfolioSkillResource,
  PortfolioSkillCategoryResource,
  PortfolioSkillsStoreBody,
  PortfolioSkillsUpdateBody,
  PortfolioSkillCategoryRequest,
  ConnectSkillEducationLevelsRequest,
  PublicPortfolioSkillResource,
} from "api/generated";
import {
  teacherSkillFromDto,
  skillCategoryFromDto,
  publicSkillFromDto,
} from "portfolio/api/mappers";
import type {
  TeacherSkill,
  PortfolioSkillCategory,
  BaseSkill,
} from "portfolio/types";

export const fetchPortfolioSkills = async (
  options: { archived?: boolean } = {},
): Promise<TeacherSkill[]> => {
  const params = options.archived === true ? "?archived=true" : "";
  const {
    data: { data: skills },
  } = await apiService.get<{ data: TeacherPortfolioSkillResource[] }>(
    `api/portfolio/skills${params}`,
  );

  return skills.map(teacherSkillFromDto);
};

export const createSkill = async (
  data: PortfolioSkillsStoreBody,
): Promise<TeacherSkill> => {
  const { data: skill } = await apiService.post<TeacherPortfolioSkillResource>(
    "api/portfolio/skills",
    data,
  );

  return teacherSkillFromDto(skill);
};

export const updateSkill = async (
  skillId: number,
  data: PortfolioSkillsUpdateBody,
): Promise<TeacherSkill> => {
  const { data: skill } = await apiService.put<TeacherPortfolioSkillResource>(
    `api/portfolio/skills/${skillId}`,
    data,
  );

  return teacherSkillFromDto(skill);
};

export const deleteSkill = async (skillId: number): Promise<void> => {
  await apiService.delete(`api/portfolio/skills/${skillId}`);
};

export const archiveSkill = async (skillId: number): Promise<TeacherSkill> => {
  const { data: skill } = await apiService.put<TeacherPortfolioSkillResource>(
    `api/portfolio/skills/${skillId}/archive`,
  );

  return teacherSkillFromDto(skill);
};

export const restoreSkill = async (skillId: number): Promise<TeacherSkill> => {
  const { data: skill } = await apiService.put<TeacherPortfolioSkillResource>(
    `api/portfolio/skills/${skillId}/restore`,
  );

  return teacherSkillFromDto(skill);
};

export const connectSkillEducationLevels = async (
  skillId: number,
  educationLevels: string[],
): Promise<TeacherSkill> => {
  const { data: skill } = await apiService.post<TeacherPortfolioSkillResource>(
    `api/portfolio/skills/${skillId}/connect-education-levels`,
    { education_levels: educationLevels } as ConnectSkillEducationLevelsRequest,
  );

  return teacherSkillFromDto(skill);
};

export const fetchSkillCategories = async (): Promise<
  PortfolioSkillCategory[]
> => {
  const {
    data: { data: categories },
  } = await apiService.get<{ data: PortfolioSkillCategoryResource[] }>(
    "api/portfolio/skill-categories",
  );

  return categories.map(skillCategoryFromDto);
};

export const createSkillCategory = async (
  data: PortfolioSkillCategoryRequest,
): Promise<PortfolioSkillCategory> => {
  const { data: category } =
    await apiService.post<PortfolioSkillCategoryResource>(
      "api/portfolio/skill-categories",
      data,
    );

  return skillCategoryFromDto(category);
};

export const updateSkillCategory = async (
  categoryId: number,
  data: PortfolioSkillCategoryRequest,
): Promise<PortfolioSkillCategory> => {
  const { data: category } =
    await apiService.put<PortfolioSkillCategoryResource>(
      `api/portfolio/skill-categories/${categoryId}`,
      data,
    );

  return skillCategoryFromDto(category);
};

export const deleteSkillCategory = async (
  categoryId: number,
): Promise<void> => {
  await apiService.delete(`api/portfolio/skill-categories/${categoryId}`);
};

export const fetchPublicPortfolioSkills = async (): Promise<BaseSkill[]> => {
  const {
    data: { data: skills },
  } = await apiService.get<{ data: PublicPortfolioSkillResource[] }>(
    "/api/public-portfolio-skills",
  );

  return skills.map(publicSkillFromDto);
};

export const fetchPublicPortfolioSkillCategories = async (): Promise<
  PortfolioSkillCategory[]
> => {
  const {
    data: { data: categories },
  } = await apiService.get<{ data: PortfolioSkillCategoryResource[] }>(
    "/api/public-portfolio-skill-categories",
  );

  return categories.map(skillCategoryFromDto);
};
